import { TagProps } from '@chakra-ui/react';
import { DrawerType, QuoteStatus, RequestStatus } from '@pelicargo/types';
import { PaymentStatus, ShipmentStatus } from '@prisma/client';

import { EmailTemplateName } from '../../../api/src/lib/codegen/email';
import { DayOfWeek } from '../../../api/src/lib/kysley/enums';

// TODO: delete this
export type options = Record<string, string>;

type QuoteStatusColorScheme = Record<QuoteStatus, string>;
export const quoteStatusColorScheme: QuoteStatusColorScheme = {
  [QuoteStatus.Pending]: 'gray',
  [QuoteStatus.SentToCarrier]: 'orange',
  [QuoteStatus.ReadyForQC]: 'yellow',
  [QuoteStatus.InProgress]: 'green',
  [QuoteStatus.BookingPending]: 'blue',
  [QuoteStatus.BookingRequested]: 'blue',
  [QuoteStatus.Confirmed]: 'blue',
  [QuoteStatus.Expired]: 'red',
};

export const requestStatusUI = {
  [RequestStatus.New]: {
    colorScheme: 'purple',
    variant: 'subtle',
  },
  [RequestStatus.QuotingInProgress]: {
    colorScheme: 'green',
    variant: 'subtle',
  },
  [RequestStatus.QuotingPending]: {
    colorScheme: 'green',
    variant: 'outline',
  },
  [RequestStatus.QuotingComplete]: {
    colorScheme: 'green',
    variant: 'ghost',
  },
  [RequestStatus.BookingRequested]: {
    colorScheme: 'blue',
    variant: 'subtle',
  },
  [RequestStatus.BookingPending]: {
    colorScheme: 'blue',
    variant: 'outline',
  },
  [RequestStatus.BookingChange]: {
    colorScheme: 'yellow',
    variant: 'subtle',
  },
  [RequestStatus.BookingCancel]: {
    colorScheme: 'red',
    variant: 'subtle',
  },
  [RequestStatus.BookingComplete]: {
    colorScheme: 'blue',
    variant: 'ghost',
  },
  [RequestStatus.Cancelled]: {
    colorScheme: 'red',
    variant: 'ghost',
  },
  [RequestStatus.InfoPending]: {
    colorScheme: 'yellow',
    variant: 'outline',
  },
} satisfies Record<RequestStatus, TagProps>;

export const shipmentStatusUI = {
  [ShipmentStatus.AIRLINE_ACCEPTED]: {
    colorScheme: 'green',
    variant: 'subtle',
  },
  [ShipmentStatus.ARRIVED]: {
    colorScheme: 'green',
    variant: 'subtle',
  },
  [ShipmentStatus.AT_WAREHOUSE]: {
    colorScheme: 'yellow',
    variant: 'subtle',
  },
  [ShipmentStatus.DELIVERED]: {
    colorScheme: 'green',
    variant: 'subtle',
  },
  [ShipmentStatus.DEPARTED]: {
    colorScheme: 'green',
    variant: 'subtle',
  },
  [ShipmentStatus.ON_HOLD]: {
    colorScheme: 'red',
    variant: 'subtle',
  },
  [ShipmentStatus.PENDING]: {
    colorScheme: 'gray',
    variant: 'subtle',
  },
  [ShipmentStatus.PICKUP_COMPLETE]: {
    colorScheme: 'green',
    variant: 'subtle',
  },
  [ShipmentStatus.PICKUP_SCHEDULED]: {
    colorScheme: 'yellow',
    variant: 'subtle',
  },
  [ShipmentStatus.OUT_FOR_DELIVERY]: {
    colorScheme: 'green',
    variant: 'subtle',
  },
} satisfies Record<ShipmentStatus, TagProps>;

export const paymentStatusUI = {
  [PaymentStatus.INVOICED]: {
    colorScheme: 'yellow',
    variant: 'subtle',
  },
  [PaymentStatus.NON_APPLICABLE]: {
    colorScheme: 'gray',
    variant: 'subtle',
  },
  [PaymentStatus.OVERDUE]: {
    colorScheme: 'red',
    variant: 'subtle',
  },
  [PaymentStatus.PAID]: {
    colorScheme: 'green',
    variant: 'subtle',
  },
  [PaymentStatus.PENDING]: {
    colorScheme: 'gray',
    variant: 'subtle',
  },
  [PaymentStatus.READY_TO_INVOICE]: {
    colorScheme: 'red',
    variant: 'subtle',
  },
} satisfies Record<PaymentStatus, TagProps>;

export const greetingMessages = [
  "I hope you're having a great day.",
  'I hope all is well.',
  "I hope you're having an excellent day.",
  "I hope you're having a wonderful day.",
  "I hope you're having a fantastic day.",
  "I hope you're having a terrific day.",
  "I hope you're having a good day.",
  "I hope you're having a nice day.",
];

export const EmailOpenerTemplates = {
  [EmailTemplateName.AIRLINE_QUOTING]: `{{account_name}} has requested my services to procure rates on their behalf for the below request.`,
  [EmailTemplateName.TRUCKING_QUOTING]: `Can you please quote the below?\n• Pickup\n• Doc Print\n• Airport Transfer`,
  [EmailTemplateName.WAREHOUSE_QUOTING]: `Can you please quote the below?\n• Receive\n• Doc Print\n• Airport Transfer`,
  [EmailTemplateName.AIRLINE_BOOKING]: `We're happy to inform you that {{airline_name}} has been awarded this cargo. Please see the below booking request from {{account_name}}.`,
};

export enum ContactTags {
  'Bookings' = 'Bookings',
  'Sales' = 'Sales',
  'Operations' = 'Operations',
  'Pelicargo' = 'Pelicargo',
  'TempControl' = 'Temp Control',
}

export const CONTACT_TAGS = Object.values(ContactTags);

export const NEXT_REQUEST_STATUSES = {
  [RequestStatus.New]: RequestStatus.QuotingInProgress,
  [RequestStatus.QuotingInProgress]: RequestStatus.QuotingPending,
  [RequestStatus.QuotingPending]: RequestStatus.QuotingComplete,
  [RequestStatus.QuotingComplete]: RequestStatus.QuotingInProgress,
  [RequestStatus.BookingRequested]: RequestStatus.BookingPending,
  [RequestStatus.BookingPending]: RequestStatus.BookingComplete,
  [RequestStatus.BookingComplete]: RequestStatus.BookingPending,
  [RequestStatus.BookingChange]: RequestStatus.BookingPending,
  [RequestStatus.BookingCancel]: RequestStatus.QuotingComplete,
  [RequestStatus.InfoPending]: RequestStatus.QuotingInProgress,
  [RequestStatus.Cancelled]: RequestStatus.QuotingInProgress,
} satisfies Record<RequestStatus, RequestStatus>;

export const NEXT_SHIPMENT_STATUSES_AFR = {
  PENDING: null,
  PICKUP_SCHEDULED: null,
  PICKUP_COMPLETE: null,
  AT_WAREHOUSE: null,
  AIRLINE_ACCEPTED: ShipmentStatus.DEPARTED,
  OUT_FOR_DELIVERY: null,
  DEPARTED: ShipmentStatus.ARRIVED,
  ARRIVED: ShipmentStatus.DELIVERED,
  DELIVERED: null,
  ON_HOLD: null,
} satisfies Record<ShipmentStatus, ShipmentStatus | null>;

export const NEXT_SHIPMENT_STATUSES_FCA = {
  PENDING: ShipmentStatus.AT_WAREHOUSE,
  PICKUP_SCHEDULED: ShipmentStatus.PICKUP_COMPLETE,
  PICKUP_COMPLETE: ShipmentStatus.OUT_FOR_DELIVERY,
  AT_WAREHOUSE: ShipmentStatus.OUT_FOR_DELIVERY,
  AIRLINE_ACCEPTED: ShipmentStatus.DEPARTED,
  OUT_FOR_DELIVERY: ShipmentStatus.AIRLINE_ACCEPTED,
  DEPARTED: ShipmentStatus.ARRIVED,
  ARRIVED: ShipmentStatus.DELIVERED,
  DELIVERED: null,
  ON_HOLD: null,
} satisfies Record<ShipmentStatus, ShipmentStatus | null>;

export const NEXT_SHIPMENT_STATUSES_EXW = {
  PENDING: ShipmentStatus.PICKUP_SCHEDULED,
  PICKUP_SCHEDULED: ShipmentStatus.PICKUP_COMPLETE,
  PICKUP_COMPLETE: ShipmentStatus.OUT_FOR_DELIVERY,
  AT_WAREHOUSE: ShipmentStatus.OUT_FOR_DELIVERY,
  OUT_FOR_DELIVERY: ShipmentStatus.AIRLINE_ACCEPTED,
  AIRLINE_ACCEPTED: ShipmentStatus.DEPARTED,
  DEPARTED: ShipmentStatus.ARRIVED,
  ARRIVED: ShipmentStatus.DELIVERED,
  DELIVERED: null,
  ON_HOLD: null,
} satisfies Record<ShipmentStatus, ShipmentStatus | null>;

export const NEXT_PAYMENT_STATUSES = {
  READY_TO_INVOICE: PaymentStatus.INVOICED,
  INVOICED: PaymentStatus.PAID,
  NON_APPLICABLE: PaymentStatus.READY_TO_INVOICE,
  OVERDUE: PaymentStatus.PAID,
  PAID: null,
  PENDING: PaymentStatus.READY_TO_INVOICE,
} satisfies Record<PaymentStatus, PaymentStatus | null>;

export const NEXT_QUOTE_STATUSES = {
  [QuoteStatus.Pending]: QuoteStatus.ReadyForQC,
  [QuoteStatus.SentToCarrier]: QuoteStatus.ReadyForQC,
  [QuoteStatus.ReadyForQC]: QuoteStatus.InProgress,
  [QuoteStatus.InProgress]: QuoteStatus.Pending,
  [QuoteStatus.BookingRequested]: QuoteStatus.BookingPending,
  [QuoteStatus.BookingPending]: QuoteStatus.Confirmed,
  [QuoteStatus.Confirmed]: QuoteStatus.InProgress,
  [QuoteStatus.Expired]: QuoteStatus.InProgress,
} satisfies Record<QuoteStatus, QuoteStatus>;

export const NEXT_EMAIL_DRAWERS = {
  [RequestStatus.New]: DrawerType.ProviderQuoting,
  [RequestStatus.QuotingInProgress]: DrawerType.ProviderQuoting,
  [RequestStatus.QuotingPending]: DrawerType.QuoteUpdate,
  [RequestStatus.QuotingComplete]: DrawerType.NewRate,
  [RequestStatus.BookingRequested]: DrawerType.AirlineBooking,
  [RequestStatus.BookingPending]: DrawerType.BookingConfirmation,
  [RequestStatus.BookingComplete]: DrawerType.BookingConfirmation,
  [RequestStatus.BookingChange]: DrawerType.BookingChange,
  [RequestStatus.BookingCancel]: DrawerType.BookingCancel,
  [RequestStatus.InfoPending]: DrawerType.ProviderQuoting,
  [RequestStatus.Cancelled]: DrawerType.ProviderQuoting,
} satisfies Record<RequestStatus, DrawerType>;

export const DAYS_OF_WEEK = [
  DayOfWeek.SUNDAY,
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
];
